import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getConsultationPath } from "../../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../../constants/webLeadModules";
import {
  ArrowLeft,
  Calculator,
  RotateCcw,
  IndianRupee,
  Percent,
  Clock,
  ArrowDownCircle,
  ArrowRightLeft,
  TrendingUp,
  GitCompare,
  Sliders,
} from "lucide-react";
import Navbar from "../../../components/Navbar/Navbar";
import Footer from "../../../components/Footer/Footer";
import Swal from "sweetalert2";
import "./AdvancedMFCalculator.css";

const STRATEGIES = [
  {
    value: "swp",
    label: "SWP",
    fullLabel: "Systematic Withdrawal Plan",
    desc: "Draw regular income from corpus",
    color: "#2563eb",
    colorEnd: "#60a5fa",
    Icon: ArrowDownCircle,
  },
  {
    value: "stp",
    label: "STP",
    fullLabel: "Systematic Transfer Plan",
    desc: "Transfer + invest simultaneously",
    color: "#7c3aed",
    colorEnd: "#a78bfa",
    Icon: ArrowRightLeft,
  },
  {
    value: "cagr",
    label: "CAGR",
    fullLabel: "CAGR Return Calculator",
    desc: "Find compound annual growth rate",
    color: "#0891b2",
    colorEnd: "#38bdf8",
    Icon: TrendingUp,
  },
  {
    value: "swpfd",
    label: "SWP vs FD",
    fullLabel: "SWP vs Fixed Deposit",
    desc: "Compare withdrawal vs FD returns",
    color: "#f59e0b",
    colorEnd: "#fcd34d",
    Icon: GitCompare,
  },
];

const formatINR = (n) =>
  Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function AdvancedMFCalculator() {
  const [strategy, setStrategy] = useState("swp");
  const [initial, setInitial] = useState("");
  const [rate, setRate] = useState("");
  const [years, setYears] = useState("");
  const [withdrawal, setWithdrawal] = useState("");
  const [sip, setSip] = useState("");
  const [beginValue, setBeginValue] = useState("");
  const [endValue, setEndValue] = useState("");
  const [result, setResult] = useState(null);

  const active = STRATEGIES.find((s) => s.value === strategy);
  const accentColor = active?.color || "#2563eb";
  const accentEnd = active?.colorEnd || "#60a5fa";

  /* ── Calculations with Monthly Loops for Yearly Breakdown ── */
  const calcSWP = () => {
    const P0 = Number(initial);
    const r_m = Number(rate) / 100 / 12;
    const n = Number(years);
    const W = Number(withdrawal);

    let bal = P0;
    let totalWithdrawn = 0;
    const breakdown = [];

    for (let y = 1; y <= n; y++) {
      let yInt = 0;
      let yW = 0;
      for (let m = 0; m < 12; m++) {
        let ip = bal * r_m;
        yInt += ip;
        bal += ip;
        let wd = Math.min(bal, W);
        bal -= wd;
        yW += wd;
        totalWithdrawn += wd;
      }
      breakdown.push({
        year: y,
        interest: Math.round(yInt),
        withdrawn: Math.round(yW),
        balance: Math.round(Math.max(0, bal)),
      });
    }

    return {
      FV: Math.round(bal),
      totalInvestment: P0,
      growth: Math.round(bal + totalWithdrawn - P0),
      totalWithdrawn: Math.round(totalWithdrawn),
      breakdown,
    };
  };

  const calcSTP = () => {
    const P0 = Number(initial);
    const r_m = Number(rate) / 100 / 12;
    const n = Number(years);
    const W = Number(withdrawal);
    const S = Number(sip);

    let swpBal = P0;
    let sipBal = 0;
    let totalSIP = 0;
    const breakdown = [];

    for (let y = 1; y <= n; y++) {
      for (let m = 0; m < 12; m++) {
        // Source Fund (SWP out)
        let ip1 = swpBal * r_m;
        swpBal += ip1;
        let wd = Math.min(swpBal, W);
        swpBal -= wd;

        // Destination Fund (SIP in)
        sipBal += S;
        let ip2 = sipBal * r_m;
        sipBal += ip2;
        totalSIP += S;
      }
      breakdown.push({
        year: y,
        swpBalance: Math.round(Math.max(0, swpBal)),
        sipBalance: Math.round(sipBal),
        total: Math.round(Math.max(0, swpBal) + sipBal),
      });
    }

    const FV = Math.max(0, swpBal) + sipBal;
    const totalInvest = P0 + totalSIP;
    return {
      FV: Math.round(FV),
      totalInvestment: Math.round(totalInvest),
      growth: Math.round(FV - totalInvest),
      breakdown,
    };
  };

  const calcCAGR = () => {
    const BV = Number(beginValue);
    const EV = Number(endValue);
    const n = Number(years);
    if (BV <= 0 || EV <= 0 || n <= 0) return null;
    return ((Math.pow(EV / BV, 1 / n) - 1) * 100).toFixed(2);
  };

  const calcSWPvsFD = () => {
    const P0 = Number(initial);
    const r_m = Number(rate) / 100 / 12;
    const n = Number(years);
    const W = Number(withdrawal);

    let swpBal = P0;
    let fdBal = P0;
    let swpTotalW = 0;
    const breakdown = [];

    for (let y = 1; y <= n; y++) {
      for (let m = 0; m < 12; m++) {
        // SWP Logic
        let ipSWP = swpBal * r_m;
        swpBal += ipSWP;
        let wd = Math.min(swpBal, W);
        swpBal -= wd;
        swpTotalW += wd;

        // FD Logic (Compounds monthly, no withdrawal)
        let ipFD = fdBal * r_m;
        fdBal += ipFD;
      }
      breakdown.push({
        year: y,
        swpBalance: Math.round(Math.max(0, swpBal)),
        fdBalance: Math.round(fdBal),
        diff: Math.round(Math.max(0, swpBal) - fdBal),
      });
    }

    return {
      swp: {
        FV: Math.round(swpBal),
        totalInvestment: P0,
        growth: Math.round(swpBal + swpTotalW - P0),
      },
      fd: {
        FV: Math.round(fdBal),
        totalInvestment: P0,
        growth: Math.round(fdBal - P0),
      },
      breakdown,
    };
  };

  const handleCalculate = () => {
    if (strategy === "cagr" && (!beginValue || !endValue || !years)) {
      showError("Please fill in Beginning Value, Ending Value and Years.");
      return;
    } else if (strategy !== "cagr" && (!initial || !rate || !years)) {
      showError("Please fill in Initial Investment, Rate and Years.");
      return;
    }

    let output = null;
    switch (strategy) {
      case "swp":
        output = { type: "single", label: "SWP Result", data: calcSWP() };
        break;
      case "stp":
        output = { type: "single", label: "STP Result", data: calcSTP() };
        break;
      case "cagr":
        output = { type: "cagr", cagr: calcCAGR() };
        break;
      case "swpfd":
        output = { type: "compare", data: calcSWPvsFD() };
        break;
      default:
        break;
    }
    setResult(output);
  };

  const showError = (text) => {
    Swal.fire({
      title: "Missing Details",
      text: text,
      icon: "warning",
      confirmButtonText: "Got it",
      confirmButtonColor: accentColor,
      width: 380,
    });
  };

  const handleReset = () => {
    setInitial("");
    setRate("");
    setYears("");
    setWithdrawal("");
    setSip("");
    setBeginValue("");
    setEndValue("");
    setResult(null);
  };

  const needsInitial = ["swp", "stp", "swpfd"].includes(strategy);
  const needsWithdrawal = ["swp", "stp", "swpfd"].includes(strategy);
  const needsSip = strategy === "stp";
  const needsCAGR = strategy === "cagr";

  return (
    <div className="am-wrapper">
      <Navbar />

      {/* ── Hero ── */}
      <section className="am-hero">
        <div className="am-hero-bg" />
        <div className="am-hero-dots" />
        <div className="am-hero-glow am-glow-l" />
        <div className="am-hero-glow am-glow-r" />

        <div className="am-hero-inner">
          <Link to="/calculators" className="am-back-btn">
            <ArrowLeft size={14} /> Back to Calculators
          </Link>

          <div className="am-hero-top">
            <div>
              <div className="am-eyebrow">
                <Sliders size={12} /> Advanced MF Strategy Tools
              </div>
              <h1 className="am-hero-title">
                Master Advanced
                <br />
                <span
                  className="am-gradient-text"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                >
                  Investment Strategies
                </span>
              </h1>
              <p className="am-hero-sub">
                SWP, STP, CAGR and SWP vs FD — powerful tools for sophisticated
                investors who want full control of their portfolio.
              </p>
            </div>

            <div
              className="am-hero-icon-box"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentEnd})`,
              }}
            >
              {active ? (
                <active.Icon size={36} color="#fff" />
              ) : (
                <Sliders size={36} color="#fff" />
              )}
            </div>
          </div>

          {/* Strategy chips */}
          <div className="am-strategy-chips">
            {STRATEGIES.map(({ value, label, desc, color, Icon }) => (
              <button
                key={value}
                className={`am-chip${strategy === value ? " am-chip--active" : ""}`}
                style={
                  strategy === value
                    ? { borderColor: color, background: `${color}14`, color }
                    : {}
                }
                onClick={() => {
                  setStrategy(value);
                  setResult(null);
                }}
              >
                <Icon size={13} />
                <span>{label}</span>
                <span className="am-chip-sub">{desc}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Main ── */}
      <div className="am-content">
        {/* Input Card */}
        <div className="am-card">
          <div
            className="am-card-bar"
            style={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
            }}
          />
          <div className="am-card-body">
            {/* Active strategy pill */}
            <div
              className="am-active-pill"
              style={{
                background: `${accentColor}10`,
                borderColor: `${accentColor}22`,
                color: accentColor,
              }}
            >
              {active && <active.Icon size={13} />}
              {active?.fullLabel} — {active?.desc}
            </div>

            <div className="am-section-title">
              Strategy Inputs
              <span className="am-section-line" />
            </div>

            <div className="am-grid-2">
              {/* Initial Investment */}
              {needsInitial && (
                <div className="am-field">
                  <label className="am-label">
                    <IndianRupee size={12} /> Initial Investment (₹)
                  </label>
                  <div className="am-input-wrap">
                    <span className="am-input-icon">
                      <IndianRupee size={14} />
                    </span>
                    <input
                      type="number"
                      className="am-input"
                      placeholder="e.g., 1000000"
                      value={initial}
                      onChange={(e) => setInitial(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Withdrawal Amount */}
              {needsWithdrawal && (
                <div className="am-field">
                  <label className="am-label">
                    <ArrowDownCircle size={12} /> Monthly Withdrawal (₹)
                  </label>
                  <div className="am-input-wrap">
                    <span className="am-input-icon">
                      <IndianRupee size={14} />
                    </span>
                    <input
                      type="number"
                      className="am-input"
                      placeholder="e.g., 5000"
                      value={withdrawal}
                      onChange={(e) => setWithdrawal(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* SIP Amount for STP */}
              {needsSip && (
                <div className="am-field">
                  <label className="am-label">
                    <TrendingUp size={12} /> Monthly SIP Addition (₹)
                  </label>
                  <div className="am-input-wrap">
                    <span className="am-input-icon">
                      <IndianRupee size={14} />
                    </span>
                    <input
                      type="number"
                      className="am-input"
                      placeholder="e.g., 2000"
                      value={sip}
                      onChange={(e) => setSip(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* CAGR Inputs */}
              {needsCAGR && (
                <>
                  <div className="am-field">
                    <label className="am-label">
                      <IndianRupee size={12} /> Beginning Value (₹)
                    </label>
                    <div className="am-input-wrap">
                      <span className="am-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="am-input"
                        placeholder="e.g., 100000"
                        value={beginValue}
                        onChange={(e) => setBeginValue(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="am-field">
                    <label className="am-label">
                      <IndianRupee size={12} /> Ending Value (₹)
                    </label>
                    <div className="am-input-wrap">
                      <span className="am-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="am-input"
                        placeholder="e.g., 200000"
                        value={endValue}
                        onChange={(e) => setEndValue(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Expected Return */}
              {strategy !== "cagr" && (
                <div className="am-field">
                  <label className="am-label">
                    <Percent size={12} /> Expected Annual Return (%)
                  </label>
                  <div className="am-input-wrap">
                    <span className="am-input-icon">
                      <Percent size={14} />
                    </span>
                    <input
                      type="number"
                      className="am-input"
                      placeholder="e.g., 12"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Duration */}
              <div className="am-field">
                <label className="am-label">
                  <Clock size={12} /> Duration (Years)
                </label>
                <div className="am-input-wrap">
                  <span className="am-input-icon">
                    <Clock size={14} />
                  </span>
                  <input
                    type="number"
                    className="am-input"
                    placeholder="e.g., 10"
                    value={years}
                    onChange={(e) => setYears(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="am-btn-row">
              <button
                className="am-calc-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
                onClick={handleCalculate}
              >
                <Calculator size={16} /> Calculate Strategy
              </button>
              <button className="am-reset-btn" onClick={handleReset}>
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ── Results ── */}
        {result && (
          <>
            <div className="am-card">
              <div
                className="am-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="am-card-body">
                {/* ── CAGR Result ── */}
                {result.type === "cagr" && (
                  <>
                    <div className="am-section-title">
                      CAGR Result
                      <span className="am-section-line" />
                    </div>
                    <div
                      className="am-cagr-hero"
                      style={{
                        borderColor: `${accentColor}22`,
                        background: `${accentColor}08`,
                      }}
                    >
                      <div className="am-cagr-label">
                        Compound Annual Growth Rate
                      </div>
                      <div
                        className="am-cagr-value"
                        style={{ color: accentColor }}
                      >
                        {result.cagr}%
                      </div>
                      <div className="am-cagr-sub">
                        Your investment grew from{" "}
                        <strong>₹ {formatINR(beginValue)}</strong> to{" "}
                        <strong>₹ {formatINR(endValue)}</strong> in{" "}
                        <strong>{years} years</strong>
                      </div>
                    </div>
                  </>
                )}

                {/* ── Single Result (SWP / STP) ── */}
                {result.type === "single" && (
                  <>
                    <div className="am-section-title">
                      {result.label} Summary
                      <span className="am-section-line" />
                    </div>
                    <div className="am-result-grid">
                      {[
                        {
                          label: "Final Portfolio Value",
                          value: result.data.FV,
                          color: accentColor,
                        },
                        {
                          label: "Total Invested",
                          value: result.data.totalInvestment,
                          color: "#0891b2",
                        },
                        {
                          label: "Total Growth Earned",
                          value: result.data.growth,
                          color: "#10b981",
                        },
                        ...(strategy === "swp"
                          ? [
                              {
                                label: "Total Withdrawn",
                                value: result.data.totalWithdrawn,
                                color: "#f59e0b",
                              },
                            ]
                          : []),
                      ].map(({ label, value, color }) => (
                        <div
                          key={label}
                          className="am-result-card"
                          style={{
                            background: `linear-gradient(135deg, ${color}08, ${color}16)`,
                            borderColor: `${color}28`,
                          }}
                        >
                          <div className="am-result-label">{label}</div>
                          <div className="am-result-value" style={{ color }}>
                            ₹ {formatINR(value)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Breakdown bar for STP */}
                    {strategy === "stp" && result.data.FV > 0 && (
                      <>
                        <div className="am-section-title am-section-title--spaced">
                          Portfolio Breakdown
                          <span className="am-section-line" />
                        </div>
                        {(() => {
                          const invPct = Math.max(
                            0,
                            Math.min(
                              100,
                              Math.round(
                                (result.data.totalInvestment / result.data.FV) *
                                  100,
                              ),
                            ),
                          );
                          const groPct = 100 - invPct;
                          return [
                            {
                              label: "Total Invested",
                              pct: invPct,
                              amt: result.data.totalInvestment,
                              color: "#0891b2",
                            },
                            {
                              label: "Returns / Growth",
                              pct: groPct,
                              amt: result.data.growth,
                              color: accentColor,
                            },
                          ].map(({ label, pct, amt, color }) => (
                            <div key={label} className="am-bar-group">
                              <div className="am-bar-row">
                                <span className="am-bar-label">
                                  {label} ({pct}%)
                                </span>
                                <span className="am-bar-amt">
                                  ₹ {formatINR(amt)}
                                </span>
                              </div>
                              <div className="am-bar-track">
                                <div
                                  className="am-bar-fill"
                                  style={{
                                    width: `${pct}%`,
                                    background: `linear-gradient(90deg, ${color}, ${color}88)`,
                                  }}
                                />
                              </div>
                            </div>
                          ));
                        })()}
                      </>
                    )}
                  </>
                )}

                {/* ── SWP vs FD Comparison ── */}
                {result.type === "compare" && (
                  <>
                    <div className="am-section-title">
                      SWP vs Fixed Deposit Output
                      <span className="am-section-line" />
                    </div>

                    <div className="am-compare-grid">
                      {/* SWP col */}
                      <div className="am-compare-col">
                        <div
                          className="am-compare-badge"
                          style={{
                            background: "#2563eb14",
                            color: "#2563eb",
                            borderColor: "#2563eb22",
                          }}
                        >
                          <ArrowDownCircle size={13} /> SWP Fund
                        </div>
                        <div
                          className="am-compare-value"
                          style={{ color: "#2563eb" }}
                        >
                          ₹ {formatINR(result.data.swp.FV)}
                        </div>
                        <div className="am-compare-sub">Final Balance</div>
                        <div className="am-compare-rows">
                          <div className="am-compare-row">
                            <span>Total Invested</span>
                            <strong>
                              ₹ {formatINR(result.data.swp.totalInvestment)}
                            </strong>
                          </div>
                          <div className="am-compare-row">
                            <span>Growth Earned</span>
                            <strong style={{ color: "#10b981" }}>
                              ₹ {formatINR(result.data.swp.growth)}
                            </strong>
                          </div>
                        </div>
                      </div>

                      {/* VS divider */}
                      <div className="am-compare-vs">VS</div>

                      {/* FD col */}
                      <div className="am-compare-col">
                        <div
                          className="am-compare-badge"
                          style={{
                            background: "#f59e0b14",
                            color: "#f59e0b",
                            borderColor: "#f59e0b22",
                          }}
                        >
                          <TrendingUp size={13} /> Fixed Deposit
                        </div>
                        <div
                          className="am-compare-value"
                          style={{ color: "#f59e0b" }}
                        >
                          ₹ {formatINR(result.data.fd.FV)}
                        </div>
                        <div className="am-compare-sub">Final Balance</div>
                        <div className="am-compare-rows">
                          <div className="am-compare-row">
                            <span>Total Invested</span>
                            <strong>
                              ₹ {formatINR(result.data.fd.totalInvestment)}
                            </strong>
                          </div>
                          <div className="am-compare-row">
                            <span>Growth Earned</span>
                            <strong style={{ color: "#10b981" }}>
                              ₹ {formatINR(result.data.fd.growth)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Winner banner */}
                    {(() => {
                      const swpWins = result.data.swp.FV >= result.data.fd.FV;
                      const diff = Math.abs(
                        result.data.swp.FV - result.data.fd.FV,
                      );
                      const winColor = swpWins ? "#2563eb" : "#f59e0b";
                      return (
                        <div
                          className="am-diff-banner"
                          style={{
                            background: `${winColor}0d`,
                            borderColor: `${winColor}22`,
                          }}
                        >
                          <GitCompare size={16} style={{ color: winColor }} />
                          <span>
                            <strong style={{ color: winColor }}>
                              {swpWins ? "SWP Strategy" : "Fixed Deposit"}
                            </strong>{" "}
                            leaves you with a higher balance by{" "}
                            <strong style={{ color: winColor }}>
                              ₹ {formatINR(diff)}
                            </strong>
                          </span>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>

            {/* ── Tabular Breakdown (For SWP, STP, and SWP vs FD) ── */}
            {result.data?.breakdown && (
              <div className="am-card">
                <div
                  className="am-card-bar"
                  style={{
                    background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                />
                <div className="am-card-body">
                  <div className="am-section-title">
                    Yearly Schedule
                    <span className="am-section-line" />
                  </div>

                  <div className="am-table-wrap">
                    <table className="am-table">
                      <thead>
                        <tr>
                          <th className="am-th">Year</th>
                          {strategy === "swp" && (
                            <>
                              <th className="am-th">Interest Earned (₹)</th>
                              <th className="am-th">Amount Withdrawn (₹)</th>
                              <th className="am-th">Closing Balance (₹)</th>
                            </>
                          )}
                          {strategy === "stp" && (
                            <>
                              <th className="am-th">Source Fund Balance (₹)</th>
                              <th className="am-th">Dest. Fund Balance (₹)</th>
                              <th className="am-th">
                                Total Portfolio Value (₹)
                              </th>
                            </>
                          )}
                          {strategy === "swpfd" && (
                            <>
                              <th className="am-th">SWP Balance (₹)</th>
                              <th className="am-th">Fixed Deposit Value (₹)</th>
                              <th className="am-th">Difference (₹)</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {result.data.breakdown.map((row, i) => (
                          <tr
                            key={row.year}
                            className={i % 2 === 0 ? "am-tr-even" : "am-tr-odd"}
                          >
                            <td className="am-td am-td-year">
                              Year {row.year}
                            </td>

                            {/* Render rows based on active strategy */}
                            {strategy === "swp" && (
                              <>
                                <td className="am-td am-td-mono am-text-success">
                                  +{formatINR(row.interest)}
                                </td>
                                <td className="am-td am-td-mono am-text-danger">
                                  -{formatINR(row.withdrawn)}
                                </td>
                                <td className="am-td am-td-mono am-td-bold">
                                  {formatINR(row.balance)}
                                </td>
                              </>
                            )}

                            {strategy === "stp" && (
                              <>
                                <td className="am-td am-td-mono">
                                  {formatINR(row.swpBalance)}
                                </td>
                                <td className="am-td am-td-mono">
                                  {formatINR(row.sipBalance)}
                                </td>
                                <td className="am-td am-td-mono am-td-bold">
                                  {formatINR(row.total)}
                                </td>
                              </>
                            )}

                            {strategy === "swpfd" && (
                              <>
                                <td className="am-td am-td-mono">
                                  {formatINR(row.swpBalance)}
                                </td>
                                <td className="am-td am-td-mono">
                                  {formatINR(row.fdBalance)}
                                </td>
                                <td
                                  className={`am-td am-td-mono ${
                                    row.diff >= 0
                                      ? "am-text-success"
                                      : "am-text-danger"
                                  }`}
                                >
                                  {row.diff > 0 ? "+" : ""}
                                  {formatINR(row.diff)}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* CTA Banner */}
        <div className="am-cta-banner">
          <div className="am-cta-text">
            <h3 className="am-cta-title">
              Want a tailored advanced MF strategy?
            </h3>
            <p className="am-cta-sub">
              Our advisors can design a personalised SWP, STP or portfolio
              rebalancing plan for your needs.
            </p>
          </div>
          <Link to={getConsultationPath(WEB_LEAD_MODULES.CALC_ADV_MF)} className="am-cta-btn">
            Book Free Consultation →
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
